import { Button, Flex, Upload } from 'antd';
import { memo, useCallback, useMemo } from 'react';

import * as Styled from './styles';
import { formatFileSize } from './utils';

type Props = {
  acceptedFormat: string;
  onFileSelect: (file: File) => void;
  file?: File | null;
  onRemove?: () => void;
  disabled?: boolean;
  hideDropzoneWhenFileSelected?: boolean;
};

const FileDropzone = ({
  acceptedFormat,
  onFileSelect,
  file,
  onRemove,
  disabled = false,
  hideDropzoneWhenFileSelected = false
}: Props) => {
  const fileSize = file?.size;
  const formattedFileSize = useMemo(() => {
    if (fileSize == null) {
      return '';
    }

    return formatFileSize(fileSize);
  }, [fileSize]);

  const handleBeforeUpload = useCallback(
    (rawFile: File) => {
      const isValidFormat = rawFile.name
        .trim()
        .toLowerCase()
        .endsWith(acceptedFormat);

      if (isValidFormat) {
        onFileSelect(rawFile);
        return false;
      }

      return Upload.LIST_IGNORE;
    },
    [acceptedFormat, onFileSelect]
  );

  return (
    <Flex flex={1} vertical>
      {(!hideDropzoneWhenFileSelected || !file) && (
        <Styled.StyledDragger
          accept={acceptedFormat}
          maxCount={1}
          showUploadList={false}
          disabled={disabled}
          beforeUpload={handleBeforeUpload}>
          <Styled.UploadIcon />
          <Styled.DropZoneTextRow>
            <Styled.DropZoneText>
              Drag &amp; drop files or click to
            </Styled.DropZoneText>
            <Styled.BrowseButton type='link'>browse</Styled.BrowseButton>
          </Styled.DropZoneTextRow>
          <Styled.SupportedFormats>
            Supported formats: <strong>{acceptedFormat}</strong>
          </Styled.SupportedFormats>
        </Styled.StyledDragger>
      )}

      {file && (
        <Styled.FileInfoWrapper>
          <Styled.ReadyToUploadText type='secondary'>
            Ready to upload
          </Styled.ReadyToUploadText>
          <Styled.FileItem>
            <Styled.FileNameRow>
              <Styled.FileIcon />
              <Styled.FileName>{file.name}</Styled.FileName>
            </Styled.FileNameRow>
            <Styled.FileActions>
              <Styled.FileSize>{formattedFileSize}</Styled.FileSize>
              {onRemove && (
                <Button type='text' aria-label='Remove file' onClick={onRemove}>
                  <Styled.RemoveFileIcon aria-hidden='true' />
                </Button>
              )}
            </Styled.FileActions>
          </Styled.FileItem>
        </Styled.FileInfoWrapper>
      )}
    </Flex>
  );
};

export default memo(FileDropzone);
